import { useEffect, useState } from "react";
import { CheckCircle2, Heart, Layers3, Play, ShoppingCart, X } from "lucide-react";
import { Link } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { useModalScrollbar } from "@/hooks/useModalScrollbar";
import { buildVimeoEmbedUrl } from "@/lib/helpers";
import type { BundleWithCourses } from "@/lib/schema";
import { cn } from "@/lib/utils";

const signatureRoutineLabel = "Signature Routine";

interface LibraryBundleModalProps {
  bundle: BundleWithCourses;
  instructorImage: string;
  isLoggedIn: boolean;
  isOwned: boolean;
  isSaved: boolean;
  inCart: boolean;
  hasCartConflict: boolean;
  ownedCourseIds: Set<string>;
  loginHref: string;
  onAddToCart: (bundle: BundleWithCourses) => void;
  onClose: () => void;
  onToggleSaved: () => void;
}

export function LibraryBundleModal({
  bundle,
  instructorImage,
  isLoggedIn,
  isOwned,
  isSaved,
  inCart,
  hasCartConflict,
  ownedCourseIds,
  loginHref,
  onAddToCart,
  onClose,
  onToggleSaved,
}: LibraryBundleModalProps) {
  const [selectedPreviewCourseId, setSelectedPreviewCourseId] = useState<string | null>(null);
  const { scrollRef: contentScrollRef, scrollbar } = useModalScrollbar([bundle.id, selectedPreviewCourseId]);
  const hasVisibleDiscount = hasVisibleDiscountPercentage(bundle.discount_percentage);
  const visibleDiscountPercentage = hasVisibleDiscount ? bundle.discount_percentage : null;

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = originalOverflow;
    };
  }, [onClose]);

  useEffect(() => {
    setSelectedPreviewCourseId(null);
  }, [bundle.id]);

  const ownedCount = bundle.courses.filter((course) => ownedCourseIds.has(course.id)).length;
  const primaryLabel = getPrimaryLabel({ isLoggedIn, isOwned, inCart, hasCartConflict });
  const instructorMeta = getBundleInstructorMeta(bundle);
  const instructorBio = getBundleInstructorBio(bundle);
  const selectedPreviewIndex = bundle.courses.findIndex((course) => course.id === selectedPreviewCourseId);
  const selectedPreviewCourse = selectedPreviewIndex >= 0 ? bundle.courses[selectedPreviewIndex] : null;
  const mediaTitle = selectedPreviewCourse?.name ?? bundle.name;
  const showVideoPreview = Boolean(selectedPreviewCourse?.preview_video_id?.video_url);
  const mediaImage = selectedPreviewCourse?.thumbnail_links_course ?? bundle.thumbnail_url;

  return (
    <>
      <button
        type="button"
        className="fixed inset-0 z-50 bg-[rgba(29,13,24,0.58)]"
        aria-label="Close signature routine preview"
        onClick={onClose}
      />
      <div
        className="fixed left-1/2 top-1/2 z-[60] grid h-[min(48rem,calc(100vh-2rem))] w-[min(74rem,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 grid-cols-[minmax(0,1fr)_minmax(0,0.95fr)] overflow-hidden rounded-[2rem] bg-white shadow-[0_40px_120px_rgba(31,11,23,0.3)] max-[991px]:max-h-[calc(100vh-1.5rem)] max-[991px]:w-[min(46rem,calc(100vw-1.5rem))] max-[991px]:grid-cols-1 max-[991px]:grid-rows-[auto_minmax(0,1fr)] max-[479px]:w-[calc(100vw-1rem)] max-[479px]:max-h-[calc(100vh-1rem)]"
        role="dialog"
        aria-modal="true"
        aria-label={`${bundle.name} preview`}
      >
        <button
          type="button"
          className="absolute right-4 top-4 z-[1] inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/92 text-[#351c2a]"
          aria-label="Close preview"
          onClick={onClose}
        >
          <X className="h-5 w-5" />
        </button>

        <div className="flex min-h-0 items-center justify-center bg-[#130a12] p-6 max-[991px]:h-[clamp(12rem,32vh,18rem)] max-[479px]:p-3">
          <div className="relative w-full overflow-hidden rounded-[1.5rem] bg-[#0f080e] shadow-[0_24px_70px_rgba(0,0,0,0.28)] [aspect-ratio:16/9]">
            {showVideoPreview && selectedPreviewCourse?.preview_video_id?.video_url ? (
              <iframe
                key={selectedPreviewCourse.id}
                src={buildVimeoEmbedUrl(selectedPreviewCourse.preview_video_id.video_url, {
                  autoplay: true,
                  muted: true,
                })}
                className="h-full w-full border-0"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                title={`Preview: ${selectedPreviewCourse.name}`}
              />
            ) : mediaImage ? (
              <img src={mediaImage} alt={mediaTitle} className="h-full w-full object-contain" />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-[radial-gradient(circle_at_top,#dca4be_0%,#91557a_45%,#4f2343_100%)] text-white">
                {selectedPreviewCourse ? <Play className="h-14 w-14" /> : <Layers3 className="h-16 w-16" />}
              </div>
            )}
          </div>
        </div>

        <div className="relative min-h-0">
          <div
            ref={contentScrollRef}
            className="modal-scrollbar flex h-full min-h-0 flex-col gap-[1.2rem] overflow-y-auto p-8 pr-[3.25rem] max-[991px]:pr-[3rem] max-[479px]:p-[1.15rem] max-[479px]:pr-[2.75rem]"
          >
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0 flex-1">
                <div className="inline-flex items-center gap-1 rounded-full bg-[#fff2fb] px-[0.8rem] py-[0.45rem] text-[0.82rem] font-[800] tracking-[-0.02em] text-[#9b4479]">
                  <Layers3 className="h-4 w-4" />
                  {signatureRoutineLabel}
                </div>
                <h2 className="mt-4 font-display text-[2rem] leading-none text-[#341928] max-[767px]:text-[1.7rem]">
                  {bundle.name}
                </h2>
                <p className="mt-4 whitespace-pre-line text-[0.98rem] leading-[1.7] text-[#5a5658]">
                  {bundle.long_description ?? bundle.short_description ?? "Curated tutorials packaged together at one set price."}
                </p>
                <div className="mt-4 flex flex-wrap gap-[0.55rem]">
                  <span className="inline-flex items-center rounded-full bg-[#f4eef2] px-[0.72rem] py-[0.38rem] text-[0.78rem] font-bold text-[#5b5258]">
                    {bundle.courses.length} tutorial{bundle.courses.length === 1 ? "" : "s"}
                  </span>
                  <span className="inline-flex items-center rounded-full bg-[#f4eef2] px-[0.72rem] py-[0.38rem] text-[0.78rem] font-bold text-[#5b5258]">
                    MP3 Music Mix
                  </span>
                  <span className="inline-flex items-center rounded-full bg-[#f4eef2] px-[0.72rem] py-[0.38rem] text-[0.78rem] font-bold text-[#5b5258]">
                    Performance Guide
                  </span>
                  {hasVisibleDiscount ? (
                    <span className="inline-flex items-center rounded-full bg-[#f4eef2] px-[0.72rem] py-[0.38rem] text-[0.78rem] font-bold text-[#5b5258]">
                      {formatDiscountPercentage(visibleDiscountPercentage!)} off
                    </span>
                  ) : null}
                </div>
              </div>

              <button
                type="button"
                className={cn(
                  "inline-flex h-6 w-6 shrink-0 items-center justify-center border-none bg-transparent text-[#3d3439] transition-colors duration-[180ms] ease-in-out hover:text-[#762a5c]",
                  isSaved && "text-[#762a5c]",
                )}
                aria-label={isSaved ? "Remove signature routine from wishlist" : "Add signature routine to wishlist"}
                onClick={onToggleSaved}
              >
                <Heart className={cn("h-4 w-4", isSaved && "fill-current")} />
              </button>
            </div>

            {ownedCount > 0 && !isOwned ? (
              <div className="rounded-[1.1rem] border border-[#d5eadb] bg-[#f4fbf6] px-4 py-3 text-[0.92rem] font-medium text-[#2c6d41]">
                {ownedCount} tutorial{ownedCount === 1 ? " is" : "s are"} already in your library. Buying this signature routine still uses the full price.
              </div>
            ) : null}

            <div className="rounded-[1.3rem] bg-[#faf6f8] p-4">
              <div className="mb-3 flex items-center justify-between gap-3">
                <div>
                  <div className="text-[0.9rem] font-semibold uppercase tracking-[0.08em] text-[#6f4f61]">
                    Included tutorials
                  </div>
                  <div className="mt-1 text-[0.88rem] text-[#7a6871]">
                    Click a tutorial to preview it in the media panel.
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                {bundle.courses.map((course, index) => {
                  const owned = ownedCourseIds.has(course.id);
                  const active = selectedPreviewCourse?.id === course.id;
                  return (
                    <button
                      type="button"
                      key={course.id}
                      className={cn(
                        "flex w-full items-start justify-between gap-3 rounded-[1rem] border bg-white px-4 py-3 text-left transition-colors duration-[180ms]",
                        active
                          ? "border-[#cfafbf] bg-[#fff8fc] shadow-[0_12px_28px_rgba(118,42,92,0.08)]"
                          : "border-white/80 hover:border-[#e2ccd7] hover:bg-[#fffafc]",
                      )}
                      aria-pressed={active}
                      aria-label={`Preview tutorial ${index + 1}: ${course.name}`}
                      onClick={() => setSelectedPreviewCourseId(course.id)}
                    >
                      <div>
                        <div className="text-[0.78rem] font-bold uppercase tracking-[0.08em] text-[#af97a4]">
                          Tutorial {index + 1}
                        </div>
                        <div className="mt-1 font-semibold text-[#341928]">{course.name}</div>
                        {(course.short_description ?? course.long_description) ? (
                          <p className="mt-1 line-clamp-2 text-[0.88rem] leading-[1.55] text-[#6a5d63]">
                            {course.short_description ?? course.long_description}
                          </p>
                        ) : null}
                      </div>
                      <div className="flex shrink-0 flex-col items-end gap-2">
                        {owned ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-[#eef8f0] px-3 py-1 text-[0.74rem] font-semibold text-[#2f7d46]">
                            <CheckCircle2 className="h-3.5 w-3.5" />
                            Owned
                          </span>
                        ) : null}
                        <span
                          className={cn(
                            "inline-flex items-center rounded-full px-3 py-1 text-[0.74rem] font-semibold",
                            active ? "bg-[#762a5c] text-white" : "bg-[#f4eef2] text-[#6b5d65]",
                          )}
                        >
                          {course.preview_video_id?.video_url ? "Play preview" : "Show cover"}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-[4rem_minmax(0,1fr)] items-start gap-4 rounded-[1.25rem] bg-[#faf6f8] p-4">
              <img
                src={instructorImage}
                alt={instructorMeta.name}
                className="h-16 w-16 rounded-[1rem] object-cover"
                loading="lazy"
              />
              <div>
                <div className="text-[0.65rem] font-bold uppercase tracking-[0.06em] text-[#b0a4aa]">{instructorMeta.label}</div>
                <div className="text-[0.96rem] font-normal text-[#2f2830]">{instructorMeta.name}</div>
                <p className="m-0 text-[0.95rem] leading-[1.65] text-[#5a5658]">{instructorBio}</p>
              </div>
            </div>

            <div className="flex flex-col gap-3">
              {!isLoggedIn && !isOwned ? (
                <Button asChild className="inline-flex h-auto min-h-[51px] w-full items-center justify-center gap-2 rounded-[1rem] border border-[#762a5c] bg-[#762a5c] px-8 py-4 text-[14px] font-medium leading-none text-white shadow-none hover:bg-[#5f2349]">
                  <Link to={loginHref}>{primaryLabel}</Link>
                </Button>
              ) : isOwned ? (
                <Button type="button" className="inline-flex h-auto min-h-[51px] w-full items-center justify-center gap-2 rounded-[1rem] border border-[#cef5ca] bg-[#cef5ca] px-8 py-4 text-[14px] font-medium leading-none text-[#114e0b] shadow-none" disabled>
                  <CheckCircle2 className="h-4 w-4" />
                  {primaryLabel}
                </Button>
              ) : (
                <Button
                  type="button"
                  className={cn(
                    "inline-flex h-auto min-h-[51px] w-full items-center justify-center gap-2 rounded-[1rem] border px-8 py-4 text-[14px] font-medium leading-none shadow-none",
                    inCart
                      ? "border-[#762a5c] bg-white text-[#762a5c] hover:bg-[#faf1f6]"
                      : hasCartConflict
                        ? "cursor-not-allowed border-[#d7c3d0] bg-[#f7f2f5] text-[#9a8b93] hover:bg-[#f7f2f5]"
                        : "border-[#762a5c] bg-[#762a5c] text-white hover:bg-[#5f2349]",
                  )}
                  disabled={hasCartConflict}
                  onClick={() => onAddToCart(bundle)}
                >
                  <ShoppingCart className="h-4 w-4" />
                  {primaryLabel}
                </Button>
              )}

              <Button
                asChild
                variant="outline"
                className="rounded-full border-[#d7c3d0] text-[#5f3350] hover:bg-[#faf1f6]"
              >
                <Link to={`/app/routine/${bundle.id}`}>View Routine Details</Link>
              </Button>
            </div>
          </div>

          {scrollbar.visible ? (
            <div className="pointer-events-none absolute inset-y-4 right-[0.95rem] w-[0.55rem]">
              <div
                className="absolute left-1/2 w-[0.38rem] -translate-x-1/2 rounded-full bg-[#9c989a]"
                style={{
                  height: `${scrollbar.thumbHeight}px`,
                  top: `${scrollbar.thumbOffset}px`,
                }}
              />
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
}

function getPrimaryLabel({
  isLoggedIn,
  isOwned,
  inCart,
  hasCartConflict,
}: {
  isLoggedIn: boolean;
  isOwned: boolean;
  inCart: boolean;
  hasCartConflict: boolean;
}) {
  if (isOwned) return "Purchased";
  if (!isLoggedIn) return "Login to Purchase";
  if (inCart) return "Added to Cart";
  if (hasCartConflict) return "Resolve Cart to Add";
  return "Add to Cart";
}

function getBundleInstructorMeta(bundle: BundleWithCourses) {
  const instructorNames = Array.from(
    new Set(
      bundle.courses
        .map((course) => course.Instructor?.name?.trim())
        .filter((name): name is string => Boolean(name)),
    ),
  );

  if (instructorNames.length === 0) {
    return { label: "Instructor", name: "Khusbu Gemmati" };
  }

  if (instructorNames.length === 1) {
    return { label: "Instructor", name: instructorNames[0] };
  }

  return {
    label: "Instructors",
    name: `${instructorNames[0]} + ${instructorNames.length - 1} more`,
  };
}

function getBundleInstructorBio(bundle: BundleWithCourses) {
  const bios = Array.from(
    new Set(
      bundle.courses
        .map((course) => course.Instructor?.description?.trim())
        .filter((description): description is string => Boolean(description)),
    ),
  );

  if (bios.length === 1) return bios[0];
  if (bios.length > 1) {
    return "This signature routine brings together approachable, joyful tutorials designed to feel performance-ready from the first watch.";
  }

  return "Dancing is my first language. Every tutorial is designed to feel approachable, joyful, and performance-ready from the first watch.";
}

function formatDiscountPercentage(value: number) {
  return Number.isInteger(value) ? `${value}%` : `${value.toFixed(2).replace(/\.?0+$/, "")}%`;
}

function hasVisibleDiscountPercentage(value: number | null) {
  return value != null && value > 0;
}
